/**
 * Catalogue centralisé des sections du configurateur pouvant être masquées
 * par le membre depuis "Paramètres de la boutique > Sections visibles".
 *
 * Convention :
 *  - clé section = identifiant stable stocké en base (Record<string, boolean>)
 *  - une clé absente du Record => la section est visible (défaut)
 *  - une clé à `false` => la section est masquée pour ce membre
 *
 * Les `titleKey` / `descKey` correspondent aux clés i18n existantes
 * dans les fichiers `utilities/locales/*.json`.
 */

export type ConfiguratorSectionGroupId =
    | 'products'
    | 'coupe'
    | 'jacketPersonalization'
    | 'vestPersonalization'
    | 'pantsPersonalization'
    | 'embroidery'
    | 'shirtConfiguration'
    | 'standaloneVestConfiguration';

export interface ConfiguratorSection {
    /** Identifiant stable persisté en base (ex: 'coupe.suitType'). */
    key: string;
    /** Clé i18n du titre de la section (réutilise les libellés du configurateur). */
    titleKey: string;
    /** Clé i18n du sous-titre / description de la section. */
    descKey?: string;
}

export interface ConfiguratorSectionGroup {
    id: ConfiguratorSectionGroupId;
    titleKey: string;
    sections: ConfiguratorSection[];
}

export const CONFIGURATOR_SECTION_GROUPS: ConfiguratorSectionGroup[] = [
    {
        id: 'products',
        titleKey: 'visibleSections.group.products',
        sections: [
            { key: 'product.suit', titleKey: 'configurator.product.suit', descKey: 'configurator.product.suitSubtitle' },
            { key: 'product.shirt', titleKey: 'configurator.product.shirt', descKey: 'configurator.product.shirtSubtitle' },
            { key: 'product.vest', titleKey: 'configurator.product.vest', descKey: 'configurator.product.vestSubtitle' },
        ],
    },
    {
        id: 'coupe',
        titleKey: 'visibleSections.group.coupe',
        sections: [
            { key: 'coupe.suitType', titleKey: 'configurator.coupe.suitType', descKey: 'configurator.coupe.suitTypeSubtitle' },
            { key: 'coupe.jacketCut', titleKey: 'configurator.coupe.jacketCut', descKey: 'configurator.coupe.jacketCutSubtitle' },
            { key: 'coupe.jacketCut.buttonholeColor', titleKey: 'configurator.coupe.buttonholeColor' },
            { key: 'coupe.pantsCut', titleKey: 'configurator.coupe.pantsCut', descKey: 'configurator.coupe.pantsCutSubtitle' },
            { key: 'coupe.pantsCut.waistHeight', titleKey: 'configurator.coupe.waistHeight' },
            { key: 'coupe.pantsFabric', titleKey: 'configurator.coupe.pantsFabric', descKey: 'configurator.coupe.pantsFabricSubtitle' },
            { key: 'coupe.addVest', titleKey: 'configurator.coupe.addVest', descKey: 'configurator.coupe.addVestSubtitle' },
            { key: 'coupe.addVest.buttonholeColor', titleKey: 'configurator.coupe.buttonholeColor' },
            { key: 'coupe.vestFabric', titleKey: 'configurator.coupe.vestFabric', descKey: 'configurator.coupe.vestFabricSubtitle' },
            { key: 'coupe.vestBackFabric', titleKey: 'configurator.coupe.vestBackFabric', descKey: 'configurator.coupe.vestBackFabricSubtitle' },
        ],
    },
    {
        id: 'jacketPersonalization',
        titleKey: 'visibleSections.group.jacketPersonalization',
        sections: [
            { key: 'perso.colType', titleKey: 'configurator.perso.colType', descKey: 'configurator.perso.colTypeSubtitle' },
            { key: 'perso.colType.size', titleKey: 'configurator.perso.collarWidth' },
            { key: 'perso.jacketBottom', titleKey: 'configurator.perso.jacketBottom', descKey: 'configurator.perso.jacketBottomSubtitle' },
            { key: 'perso.chestPocket', titleKey: 'configurator.perso.chestPocket', descKey: 'configurator.perso.chestPocketSubtitle' },
            { key: 'perso.lowerPockets', titleKey: 'configurator.perso.lowerPockets', descKey: 'configurator.perso.lowerPocketsSubtitle' },
            { key: 'perso.sleeveCap', titleKey: 'configurator.perso.sleeveCap', descKey: 'configurator.perso.sleeveCapSubtitle' },
            { key: 'perso.backVents', titleKey: 'configurator.perso.backVents', descKey: 'configurator.perso.backVentsSubtitle' },
            { key: 'perso.buttons', titleKey: 'configurator.perso.buttons', descKey: 'configurator.perso.buttonsSubtitle' },
            { key: 'perso.topstitch', titleKey: 'configurator.perso.topstitch', descKey: 'configurator.perso.topstitchSubtitle' },
            { key: 'perso.topstitch.color', titleKey: 'configurator.perso.topstitchColor' },
            { key: 'perso.collarButtonhole', titleKey: 'configurator.perso.collarButtonhole', descKey: 'configurator.perso.collarButtonholeSubtitle' },
            { key: 'perso.collarButtonhole.color', titleKey: 'configurator.perso.buttonholeColor' },
            { key: 'perso.sleeveOpening', titleKey: 'configurator.perso.sleeveOpening', descKey: 'configurator.perso.sleeveOpeningSubtitle' },
            { key: 'perso.sleeveButtons', titleKey: 'configurator.perso.sleeveButtons', descKey: 'configurator.perso.sleeveButtonsSubtitle' },
            { key: 'perso.sleeveButtons.number', titleKey: 'configurator.perso.sleeveNumber' },
            { key: 'perso.sleeveButtons.overlap', titleKey: 'configurator.perso.sleeveOverlappingQuestion' },
            { key: 'perso.sleeveButtons.orientation', titleKey: 'configurator.perso.sleeveOrientation' },
            { key: 'perso.sleeveButtons.color', titleKey: 'configurator.perso.buttonholeColor' },
            { key: 'perso.sleeveButtons.position', titleKey: 'configurator.perso.sleeveButtonholePosition' },
            { key: 'perso.jacketContrast', titleKey: 'configurator.perso.jacketContrast', descKey: 'configurator.perso.jacketContrastSubtitle' },
            { key: 'perso.construction', titleKey: 'configurator.perso.construction', descKey: 'configurator.perso.constructionSubtitle' },
            { key: 'perso.interiorStyle', titleKey: 'configurator.perso.interiorStyle', descKey: 'configurator.perso.interiorStyleSubtitle' },
            { key: 'perso.jacketLining', titleKey: 'configurator.perso.jacketLining', descKey: 'configurator.perso.jacketLiningSubtitle' },
            { key: 'perso.collarFelt', titleKey: 'configurator.perso.collarFelt', descKey: 'configurator.perso.collarFeltSubtitle' },
        ],
    },
    {
        id: 'vestPersonalization',
        titleKey: 'visibleSections.group.vestPersonalization',
        sections: [
            { key: 'perso.vestButtons', titleKey: 'configurator.vest.buttons', descKey: 'configurator.vest.buttonsSubtitle' },
            { key: 'perso.vestPockets', titleKey: 'configurator.perso.vestPockets', descKey: 'configurator.perso.vestPocketsSubtitle' },
            { key: 'perso.vestLapels', titleKey: 'configurator.perso.vestLapels', descKey: 'configurator.perso.vestLapelsSubtitle' },
            { key: 'perso.vestLapels.size', titleKey: 'configurator.perso.collarWidth' },
            { key: 'perso.vestBack', titleKey: 'configurator.perso.vestBack', descKey: 'configurator.perso.vestBackSubtitle' },
            { key: 'perso.vestContrast', titleKey: 'configurator.perso.vestContrast', descKey: 'configurator.perso.vestContrastSubtitle' },
        ],
    },
    {
        id: 'pantsPersonalization',
        titleKey: 'visibleSections.group.pantsPersonalization',
        sections: [
            { key: 'perso.cornerPocket', titleKey: 'configurator.perso.cornerPocket', descKey: 'configurator.perso.cornerPocketSubtitle' },
            { key: 'perso.backPocket', titleKey: 'configurator.perso.backPocket', descKey: 'configurator.perso.backPocketSubtitle' },
            { key: 'perso.pantsBottom', titleKey: 'configurator.perso.pantsBottom', descKey: 'configurator.perso.pantsBottomSubtitle' },
            { key: 'perso.pantsBottom.reversSize', titleKey: 'configurator.perso.lapelSize' },
            { key: 'perso.pantsLining', titleKey: 'configurator.perso.pantsLining', descKey: 'configurator.perso.pantsLiningSubtitle' },
            { key: 'perso.pantsLiningFabric', titleKey: 'configurator.perso.pantsLiningFabric', descKey: 'configurator.perso.pantsLiningFabricSubtitle' },
            { key: 'perso.pantsButtons', titleKey: 'configurator.perso.pantsButtons', descKey: 'configurator.perso.pantsButtonsSubtitle' },
            { key: 'perso.pantsContrast', titleKey: 'configurator.perso.pantsContrast', descKey: 'configurator.perso.pantsContrastSubtitle' },
        ],
    },
    {
        id: 'embroidery',
        titleKey: 'visibleSections.group.embroidery',
        sections: [
            { key: 'perso.embroidery', titleKey: 'configurator.perso.embroidery', descKey: 'configurator.perso.embroiderySubtitle' },
            { key: 'perso.embroidery.target', titleKey: 'configurator.perso.embroideryTarget' },
            { key: 'perso.embroidery.sameSettings', titleKey: 'configurator.perso.embroideryUseSameSettings' },
            { key: 'perso.embroidery.jacketPosition', titleKey: 'configurator.perso.embroideryJacketPosition' },
            { key: 'perso.embroidery.pantsPosition', titleKey: 'configurator.perso.embroideryPantsPosition' },
            { key: 'perso.embroidery.vestPosition', titleKey: 'configurator.perso.embroideryVestPosition' },
            { key: 'perso.embroidery.threadColor', titleKey: 'configurator.perso.embroideryThread' },
            { key: 'perso.embroidery.size', titleKey: 'configurator.perso.embroiderySize' },
            { key: 'perso.embroidery.font', titleKey: 'configurator.perso.embroideryFont' },
        ],
    },
    {
        id: 'shirtConfiguration',
        titleKey: 'visibleSections.group.shirtConfiguration',
        sections: [
            { key: 'shirt.style', titleKey: 'configurator.shirt.style', descKey: 'configurator.shirt.styleSubtitle' },
            { key: 'shirt.fabric', titleKey: 'configurator.shirt.fabric', descKey: 'configurator.shirt.fabricSubtitle' },
        ],
    },
    {
        id: 'standaloneVestConfiguration',
        titleKey: 'visibleSections.group.standaloneVestConfiguration',
        sections: [
            { key: 'vest.cut', titleKey: 'configurator.vest.cut', descKey: 'configurator.vest.cutSubtitle' },
            { key: 'vest.buttons', titleKey: 'configurator.vest.buttons', descKey: 'configurator.vest.buttonsSubtitle' },
            { key: 'vest.fabric', titleKey: 'configurator.vest.fabric', descKey: 'configurator.vest.fabricSubtitle' },
            { key: 'vest.backFabric', titleKey: 'configurator.coupe.vestBackFabric', descKey: 'configurator.coupe.vestBackFabricSubtitle' },
            { key: 'vest.pockets', titleKey: 'configurator.vest.pockets', descKey: 'configurator.vest.pocketsSubtitle' },
            { key: 'vest.lapels', titleKey: 'configurator.vest.lapels', descKey: 'configurator.vest.lapelsSubtitle' },
            { key: 'vest.lapels.size', titleKey: 'configurator.perso.collarWidth' },
            { key: 'vest.back', titleKey: 'configurator.vest.back', descKey: 'configurator.vest.backSubtitle' },
            { key: 'vest.contrast', titleKey: 'configurator.vest.contrast', descKey: 'configurator.vest.contrastSubtitle' },
            { key: 'vest.buttonhole', titleKey: 'configurator.vest.buttonhole', descKey: 'configurator.vest.buttonholeSubtitle' },
            { key: 'vest.embroidery', titleKey: 'configurator.vest.embroidery', descKey: 'configurator.vest.embroiderySubtitle' },
            { key: 'vest.embroidery.threadColor', titleKey: 'configurator.perso.embroideryThread' },
            { key: 'vest.embroidery.size', titleKey: 'configurator.perso.embroiderySize' },
            { key: 'vest.embroidery.font', titleKey: 'configurator.perso.embroideryFont' },
        ],
    },
];

export const ALL_CONFIGURATOR_SECTION_KEYS: string[] = CONFIGURATOR_SECTION_GROUPS
    .flatMap((g) => g.sections.map((s) => s.key));

/**
 * Règles transverses : si la section `master` est masquée, alors toutes les
 * sections `dependents` sont automatiquement masquées et ne peuvent pas être
 * réactivées tant que le master ne l'est pas. Les dépendances sont résolues
 * de manière transitive.
 */
export interface SectionDependencyRule {
    master: string;
    dependents: string[];
}

export const SECTION_DEPENDENCY_RULES: SectionDependencyRule[] = [
    // Les produits désactivés disparaissent du catalogue avec toutes leurs étapes.
    {
        master: 'product.suit',
        dependents: [
            'coupe.suitType', 'coupe.jacketCut', 'coupe.pantsCut', 'coupe.pantsFabric',
            'coupe.vestFabric',
            'perso.colType', 'perso.jacketBottom', 'perso.chestPocket', 'perso.lowerPockets',
            'perso.sleeveCap', 'perso.backVents', 'perso.buttons', 'perso.topstitch',
            'perso.collarButtonhole', 'perso.sleeveOpening', 'perso.sleeveButtons',
            'perso.jacketContrast', 'perso.construction', 'perso.interiorStyle',
            'perso.jacketLining', 'perso.collarFelt',
            'perso.cornerPocket', 'perso.backPocket', 'perso.pantsBottom',
            'perso.pantsLining', 'perso.pantsLiningFabric', 'perso.pantsButtons',
            'perso.pantsContrast',
        ],
    },
    {
        master: 'product.shirt',
        dependents: ['shirt.style', 'shirt.fabric'],
    },
    {
        master: 'product.vest',
        dependents: [
            'vest.cut', 'vest.buttons', 'vest.fabric', 'vest.backFabric', 'vest.pockets', 'vest.lapels',
            'vest.back', 'vest.contrast', 'vest.buttonhole', 'vest.embroidery',
        ],
    },
    // Si l'option "Ajouter un gilet" est masquée, plus aucune option de gilet ne s'affiche.
    {
        master: 'coupe.addVest',
        dependents: [
            'coupe.vestFabric',
            'coupe.vestBackFabric',
            'perso.vestPockets',
            'perso.vestButtons',
            'perso.vestLapels',
            'perso.vestBack',
            'perso.vestContrast',
        ],
    },
    // Si la coupe de veste est masquée, toute la personnalisation veste l'est aussi.
    {
        master: 'coupe.jacketCut',
        dependents: [
            'perso.colType',
            'perso.jacketBottom',
            'perso.chestPocket',
            'perso.lowerPockets',
            'perso.sleeveCap',
            'perso.backVents',
            'perso.buttons',
            'perso.topstitch',
            'perso.collarButtonhole',
            'perso.sleeveOpening',
            'perso.sleeveButtons',
            'perso.jacketContrast',
            'perso.construction',
            'perso.interiorStyle',
            'perso.jacketLining',
            'perso.collarFelt',
        ],
    },
    // Si la coupe de pantalon est masquée, toute la personnalisation pantalon l'est aussi.
    {
        master: 'coupe.pantsCut',
        dependents: [
            'perso.cornerPocket',
            'perso.backPocket',
            'perso.pantsBottom',
            'perso.pantsLining',
            'perso.pantsLiningFabric',
            'perso.pantsButtons',
            'perso.pantsContrast',
        ],
    },
    { master: 'coupe.jacketCut', dependents: ['coupe.jacketCut.buttonholeColor'] },
    { master: 'coupe.pantsCut', dependents: ['coupe.pantsCut.waistHeight'] },
    { master: 'coupe.addVest', dependents: ['coupe.addVest.buttonholeColor'] },
    { master: 'perso.colType', dependents: ['perso.colType.size'] },
    { master: 'perso.topstitch', dependents: ['perso.topstitch.color'] },
    { master: 'perso.collarButtonhole', dependents: ['perso.collarButtonhole.color'] },
    { master: 'perso.sleeveButtons', dependents: ['perso.sleeveButtons.number', 'perso.sleeveButtons.overlap', 'perso.sleeveButtons.orientation', 'perso.sleeveButtons.color', 'perso.sleeveButtons.position'] },
    { master: 'perso.sleeveButtons.number', dependents: ['perso.sleeveButtons.overlap', 'perso.sleeveButtons.orientation'] },
    { master: 'perso.sleeveButtons.orientation', dependents: ['perso.sleeveButtons.color', 'perso.sleeveButtons.position'] },
    { master: 'perso.vestLapels', dependents: ['perso.vestLapels.size'] },
    { master: 'vest.lapels', dependents: ['vest.lapels.size'] },
    { master: 'vest.cut', dependents: ['vest.buttonhole'] },
    { master: 'perso.pantsBottom', dependents: ['perso.pantsBottom.reversSize'] },
    { master: 'perso.embroidery', dependents: ['perso.embroidery.target', 'perso.embroidery.sameSettings', 'perso.embroidery.jacketPosition', 'perso.embroidery.pantsPosition', 'perso.embroidery.vestPosition', 'perso.embroidery.threadColor', 'perso.embroidery.size', 'perso.embroidery.font'] },
    { master: 'vest.embroidery', dependents: ['vest.embroidery.threadColor', 'vest.embroidery.size', 'vest.embroidery.font'] },
];

/** Retourne, pour une section donnée, la liste de ses masters (transitivement). */
export const getMastersOf = (sectionKey: string): string[] => {
    const masters = new Set<string>();
    const stack = [sectionKey];
    while (stack.length) {
        const current = stack.pop()!;
        for (const rule of SECTION_DEPENDENCY_RULES) {
            if (rule.dependents.includes(current) && !masters.has(rule.master)) {
                masters.add(rule.master);
                stack.push(rule.master);
            }
        }
    }
    return Array.from(masters);
};

/** Retourne tous les dépendants (transitifs) d'une section. */
export const getDependentsOf = (sectionKey: string): string[] => {
    const dependents = new Set<string>();
    const stack = [sectionKey];
    while (stack.length) {
        const current = stack.pop()!;
        for (const rule of SECTION_DEPENDENCY_RULES) {
            if (rule.master === current) {
                for (const dep of rule.dependents) {
                    if (!dependents.has(dep)) {
                        dependents.add(dep);
                        stack.push(dep);
                    }
                }
            }
        }
    }
    return Array.from(dependents);
};

/**
 * Indique si une section est verrouillée (= forcée à masquée) parce qu'au moins
 * un de ses masters est lui-même masqué. Renvoie le master bloquant le cas échéant.
 */
export const findLockingMaster = (
    sectionKey: string,
    visibility: Record<string, boolean>,
): string | null => {
    for (const master of getMastersOf(sectionKey)) {
        if (visibility[master] === false) return master;
    }
    return null;
};

/**
 * Applique les règles de dépendance à une carte de visibilité :
 * tout dépendant d'un master masqué est forcé à false.
 */
export const applyDependencyRules = (
    visibility: Record<string, boolean>,
): Record<string, boolean> => {
    const next = { ...visibility };
    // Itération jusqu'à stabilisation pour gérer la transitivité.
    let changed = true;
    while (changed) {
        changed = false;
        for (const rule of SECTION_DEPENDENCY_RULES) {
            if (next[rule.master] === false) {
                for (const dep of rule.dependents) {
                    if (next[dep] !== false) {
                        next[dep] = false;
                        changed = true;
                    }
                }
            }
        }
    }
    return next;
};

/** Une section est considérée visible sauf si explicitement marquée à false. */
export const isSectionVisible = (
    sectionKey: string,
    visibleSections?: Record<string, boolean> | null,
): boolean => visibleSections?.[sectionKey] !== false;

/* =====================================================================
 *  Catalogue des items affichés à l'intérieur de chaque section.
 *  Permet au membre de masquer certains choix (ex: cacher la coupe
 *  croisée 4x2 dans la section "Coupe veste").
 *
 *  Stockage en base : { [sectionKey]: { [itemId]: false } }.
 *  Convention : un item absent du Record est visible par défaut.
 * ===================================================================== */

export interface SectionItem {
    /** Identifiant stable persisté en base (correspond à l'id du SelectionOptionItem). */
    id: string;
    /** Clé i18n du libellé (mutuellement exclusif avec `label`). */
    labelKey?: string;
    /** Libellé littéral (pour les items dont le nom n'est pas traduit, ex: codes boutons). */
    label?: string;
}

/** Catalogue par section : section.key -> liste d'items toggleables. */
const threadColorItems: SectionItem[] = [
    { id: 'match', label: 'Assortie au tissu' },
    ...Array.from({ length: 48 }, (_, index) => ({ id: `MX${String(index + 1).padStart(3, '0')}`, label: `MX${String(index + 1).padStart(3, '0')}` })),
];
const embroideryColorItems = threadColorItems.filter((item) => item.id !== 'match');
const collarSizeItems: SectionItem[] = Array.from({ length: 16 }, (_, index) => ({ id: (6 + index * 0.5).toFixed(1), label: `${(6 + index * 0.5).toFixed(1)} cm` }));
const vestCollarSizeItems: SectionItem[] = [4, 5, 6, 7].map((size) => ({ id: String(size), label: `${size} cm` }));
const embroideryFontItems: SectionItem[] = 'ABCDEFGHI'.split('').concat(['582', '583', '584', '585', '586']).map((id) => ({ id, label: id }));

export const SECTION_ITEMS: Record<string, SectionItem[]> = {
    // ----- Produits autonomes -----
    'product.suit': [],
    'product.shirt': [],
    'product.vest': [],

    // ----- Coupe -----
    'coupe.suitType': [
        { id: '2_pieces', labelKey: 'configurator.option.suitPiece.2pieces' },
        { id: '3_pieces', labelKey: 'configurator.option.suitPiece.3pieces' },
    ],
    'coupe.jacketCut': [
        { id: '1_bouton', labelKey: 'configurator.option.jacketCut.1bouton' },
        { id: '2_boutons', labelKey: 'configurator.option.jacketCut.2boutons' },
        { id: 'croise_4x2', labelKey: 'configurator.option.jacketCut.croise4x2' },
        { id: 'croise_6x2', labelKey: 'configurator.option.jacketCut.croise6x2' },
        { id: '3_boutons', labelKey: 'configurator.option.jacketCut.3boutons' },
        { id: '3x2_boutons', labelKey: 'configurator.option.jacketCut.3x2boutons' },
        { id: 'croise_4x1', labelKey: 'configurator.option.jacketCut.croise4x1' },
        { id: 'croise_6x1', labelKey: 'configurator.option.jacketCut.croise6x1' },
    ],
    'coupe.pantsCut': [
        { id: 'sans_pince', labelKey: 'configurator.option.pantalon.sansPince' },
        { id: '1_pince', labelKey: 'configurator.option.pantalon.1pince' },
        { id: '2_pinces', labelKey: 'configurator.option.pantalon.2pinces' },
    ],
    'coupe.addVest': [
        { id: '3_boutons', labelKey: 'configurator.option.vestType.3boutons' },
        { id: '4_boutons', labelKey: 'configurator.option.vestType.4boutons' },
        { id: '5_boutons', labelKey: 'configurator.option.vestType.5boutons' },
        { id: '6_boutons', labelKey: 'configurator.option.vestType.6boutons' },
        { id: '5x3_incline_pointu', labelKey: 'configurator.option.vestType.5x3InclinePointu' },
        { id: 'carre_6x3', labelKey: 'configurator.option.vestType.carre6x3' },
        { id: 'carre_8x4', labelKey: 'configurator.option.vestType.carre8x4' },
        { id: 'croise_6x3_boutons', labelKey: 'configurator.option.vestType.croise6x3' },
        { id: 'croise_8x4_boutons', labelKey: 'configurator.option.vestType.croise8x4' },
    ],
    'coupe.vestFabric': [
        { id: 'same', labelKey: 'configurator.coupe.sameFabric' },
        { id: 'different', labelKey: 'configurator.coupe.differentFabric' },
    ],
    'coupe.vestBackFabric': [
        { id: 'front', label: 'Même tissu que l’avant du gilet' },
        { id: 'lining', label: 'Doublure' },
        { id: 'same', label: 'Même doublure que la veste' },
        { id: 'other', label: 'Autre doublure' },
    ],
    'vest.backFabric': [
        { id: 'front', label: 'Même tissu que l’avant du gilet' },
        { id: 'lining', label: 'Doublure' },
        { id: 'other', label: 'Autre doublure' },
    ],
    'coupe.pantsFabric': [
        { id: 'same', labelKey: 'configurator.coupe.sameFabric' },
        { id: 'different', labelKey: 'configurator.coupe.differentFabric' },
    ],

    // ----- Personnalisation veste -----
    'shirt.style': [
        { id: 'chemise_1', labelKey: 'configurator.option.chemise.chemise1' },
        { id: 'chemise_2', labelKey: 'configurator.option.chemise.chemise2' },
    ],
    // Liasse historique F416. Ce catalogue sera remplacé par l'endpoint chemises
    // dès que la personnalisation chemise sera enrichie.
    'shirt.fabric': Array.from({ length: 61 }, (_, index) => {
        const id = `F416${String(index + 1).padStart(2, '0')}`;
        return { id, label: id };
    }),

    // ----- Gilet vendu seul -----
    'vest.cut': [
        { id: '3_boutons', labelKey: 'configurator.option.vestType.3boutons' },
        { id: '4_boutons', labelKey: 'configurator.option.vestType.4boutons' },
        { id: '5_boutons', labelKey: 'configurator.option.vestType.5boutons' },
        { id: '6_boutons', labelKey: 'configurator.option.vestType.6boutons' },
        { id: '5x3_incline_pointu', labelKey: 'configurator.option.vestType.5x3InclinePointu' },
        { id: 'carre_6x3', labelKey: 'configurator.option.vestType.carre6x3' },
        { id: 'carre_8x4', labelKey: 'configurator.option.vestType.carre8x4' },
        { id: 'croise_6x3_boutons', labelKey: 'configurator.option.vestType.croise6x3' },
        { id: 'croise_8x4_boutons', labelKey: 'configurator.option.vestType.croise8x4' },
    ],
    'vest.fabric': [],
    'vest.buttons': [
        { id: 'none', labelKey: 'configurator.option.button.default' },
        { id: 'recouvert', labelKey: 'configurator.perso.buttonsCovered' },
    ],
    'perso.vestButtons': [
        { id: 'none', labelKey: 'configurator.option.button.default' },
        { id: 'recouvert', labelKey: 'configurator.perso.buttonsCovered' },
    ],
    'vest.pockets': [
        { id: 'none', labelKey: 'configurator.option.pocheGilet.sans' },
        { id: 'passepoilee', labelKey: 'configurator.option.pocheGilet.passepoilee' },
        { id: 'double_passepoilee', labelKey: 'configurator.option.pocheGilet.doublePassepoilee' },
        { id: 'passepoilee_et_poitrine', labelKey: 'configurator.option.pocheGilet.passepoileePoitrine' },
        { id: 'poche_a_rabat', labelKey: 'configurator.option.pocheGilet.pocheARabat' },
    ],
    'vest.lapels': [
        { id: 'none', labelKey: 'configurator.option.reversGilet.sans' },
        { id: 'a_cran', labelKey: 'configurator.option.reversGilet.aCran' },
        { id: 'chale', labelKey: 'configurator.option.reversGilet.chale' },
        { id: 'en_pointe', labelKey: 'configurator.option.reversGilet.enPointe' },
        { id: 'grand_u', labelKey: 'configurator.option.reversGilet.grandU' },
        { id: 'u', labelKey: 'configurator.option.reversGilet.u' },
    ],
    'vest.back': [
        { id: 'none', labelKey: 'configurator.option.arriereGilet.sans' },
        { id: 'carre', labelKey: 'configurator.option.arriereGilet.carre' },
        { id: 'pointe', labelKey: 'configurator.option.arriereGilet.pointe' },
    ],
    'vest.contrast': [
        { id: 'col', labelKey: 'configurator.option.contrasteGilet.col' },
        { id: 'col_arriere', labelKey: 'configurator.option.contrasteGilet.colArriere' },
        { id: 'poche_basse', labelKey: 'configurator.option.contrasteGilet.pocheBasse' },
        { id: 'poche_poitrine', labelKey: 'configurator.option.contrasteGilet.pochePoitrine' },
    ],
    'vest.buttonhole': [
        { id: 'match', label: 'Assortie au tissu' },
        ...Array.from({ length: 48 }, (_, index) => {
            const id = `MX${String(index + 1).padStart(3, '0')}`;
            return { id, label: id };
        }),
    ],
    'vest.embroidery': [
        { id: 'none', label: 'Sans broderie' },
        { id: 'enabled', label: 'Avec broderie' },
    ],
    'perso.colType': [
        { id: 'col_a_cran', labelKey: 'configurator.option.colType.colACran' },
        { id: 'col_chale', labelKey: 'configurator.option.colType.colChale' },
        { id: 'col_chale_arrondi', labelKey: 'configurator.option.colType.colChaleArrondi' },
        { id: 'col_chale_fin', labelKey: 'configurator.option.colType.colChaleFin' },
        { id: 'col_en_arc', labelKey: 'configurator.option.colType.colEnArc' },
        { id: 'col_en_pointe', labelKey: 'configurator.option.colType.colEnPointe' },
    ],
    'perso.jacketBottom': [
        { id: 'round', labelKey: 'configurator.option.jacketBottom.round' },
        { id: 'straight', labelKey: 'configurator.option.jacketBottom.straight' },
        { id: 'open_quarters', labelKey: 'configurator.option.jacketBottom.openQuarters' },
    ],
    'perso.chestPocket': [
        { id: 'sans', labelKey: 'configurator.option.pochette.sans' },
        { id: 'standard', labelKey: 'configurator.option.pochette.standard' },
        { id: 'bateau', labelKey: 'configurator.option.pochette.bateau' },
        { id: 'correctif', labelKey: 'configurator.option.pochette.correctif' },
        { id: 'safari', labelKey: 'configurator.option.pochette.safari' },
    ],
    'perso.lowerPockets': [
        { id: 'standard', labelKey: 'configurator.option.stylePoche.standard' },
        { id: 'flap_&_flap', labelKey: 'configurator.option.stylePoche.flapFlap' },
        { id: 'rabat_et_double_balai', labelKey: 'configurator.option.stylePoche.rabatDoubleBalai' },
        { id: 'correctif', labelKey: 'configurator.option.stylePoche.correctif' },
        { id: 'double_balai', labelKey: 'configurator.option.stylePoche.doubleBalai' },
        { id: 'bateau', labelKey: 'configurator.option.stylePoche.bateau' },
        { id: 'rabat_incline', labelKey: 'configurator.option.stylePoche.rabatIncline' },
        { id: 'rabat_incline_et_double_balai_incline', labelKey: 'configurator.option.stylePoche.rabatInclineDoubleBalai' },
        { id: 'safari', labelKey: 'configurator.option.stylePoche.safari' },
        { id: 'uknow', labelKey: 'configurator.option.stylePoche.uknow' },
        { id: 'patch_w_right_small_patch_pkt', labelKey: 'configurator.option.stylePoche.patch' },
        { id: 'safari_2', labelKey: 'configurator.option.stylePoche.safari2' },
    ],
    'perso.sleeveCap': [
        { id: 'standard', labelKey: 'configurator.option.teteDeManche.standard' },
        { id: 'bateau', labelKey: 'configurator.option.teteDeManche.bateau' },
        { id: 'correctif', labelKey: 'configurator.option.teteDeManche.correctif' },
        { id: 'safari', labelKey: 'configurator.option.teteDeManche.safari' },
    ],
    'perso.backVents': [
        { id: 'fermer', labelKey: 'configurator.option.fenteArriere.fermee' },
        { id: '1_fente', labelKey: 'configurator.option.fenteArriere.1fente' },
        { id: '2_fentes', labelKey: 'configurator.option.fenteArriere.2fentes' },
    ],
    'perso.buttons': [
        // Les boutons proviennent du catalogue backend (/buttons/catalog).
        { id: 'none', labelKey: 'configurator.option.button.default' },
    ],
    'perso.pantsButtons': [
        { id: 'none', labelKey: 'configurator.option.button.default' },
    ],
    'perso.topstitch': [
        { id: 'none', labelKey: 'configurator.option.surpiqure.sans' },
        { id: '0.2cm', labelKey: 'configurator.option.surpiqure.02cm' },
        { id: '0.6cm', labelKey: 'configurator.option.surpiqure.06cm' },
    ],
    'perso.collarButtonhole': [
        { id: 'none', labelKey: 'configurator.option.boutonniereCol.sans' },
        { id: 'arrondi', labelKey: 'configurator.option.boutonniereCol.arrondi' },
        { id: 'droit', labelKey: 'configurator.option.boutonniereCol.droit' },
    ],
    'perso.sleeveOpening': [
        { id: 'fonctionnel', labelKey: 'configurator.option.ouvertureManche.fonctionnel' },
        { id: 'non_fonctionnel', labelKey: 'configurator.option.ouvertureManche.nonFonctionnel' },
    ],
    'perso.sleeveButtons': [
        { id: 'none', labelKey: 'configurator.option.boutonManche.sans' },
        { id: '1_droit', labelKey: 'configurator.option.boutonManche.1droit' },
        { id: '2_droit', labelKey: 'configurator.option.boutonManche.2droit' },
        { id: '3_droit', labelKey: 'configurator.option.boutonManche.3droit' },
        { id: '4_droit', labelKey: 'configurator.option.boutonManche.4droit' },
        { id: '5_droit', labelKey: 'configurator.option.boutonManche.5droit' },
        { id: '1_en_biais', labelKey: 'configurator.option.boutonManche.1enBiais' },
        { id: '2_en_biais', labelKey: 'configurator.option.boutonManche.2enBiais' },
        { id: '3_en_biais', labelKey: 'configurator.option.boutonManche.3enBiais' },
        { id: '4_en_biais', labelKey: 'configurator.option.boutonManche.4enBiais' },
        { id: '5_en_biais', labelKey: 'configurator.option.boutonManche.5enBiais' },
        { id: '2_chevauchee', labelKey: 'configurator.option.boutonManche.2chevauchee' },
        { id: '3_chevauchee', labelKey: 'configurator.option.boutonManche.3chevauchee' },
        { id: '4_chevauchee', labelKey: 'configurator.option.boutonManche.4chevauchee' },
        { id: '5_chevauchee', labelKey: 'configurator.option.boutonManche.5chevauchee' },
        { id: '2_chevauchee_en_biais', labelKey: 'configurator.option.boutonManche.2chevaucheeEnBiais' },
        { id: '3_chevauchee_en_biais', labelKey: 'configurator.option.boutonManche.3chevaucheeEnBiais' },
        { id: '4_chevauchee_en_biais', labelKey: 'configurator.option.boutonManche.4chevaucheeEnBiais' },
        { id: '5_chevauchee_en_biais', labelKey: 'configurator.option.boutonManche.5chevaucheeEnBiais' },
    ],
    'perso.jacketContrast': [
        { id: 'col', labelKey: 'configurator.option.contrasteVeste.col' },
        { id: 'col_arriere', labelKey: 'configurator.option.contrasteVeste.colArriere' },
        { id: 'bouton_avant', labelKey: 'configurator.option.contrasteVeste.boutonAvant' },
        { id: 'bouton_manche', labelKey: 'configurator.option.contrasteVeste.boutonManche' },
        { id: 'poche_basse', labelKey: 'configurator.option.contrasteVeste.pocheBasse' },
        { id: 'passe_poile_poche_basse', labelKey: 'configurator.option.contrasteVeste.passepoilPoche' },
        { id: 'poche_poitrine', labelKey: 'configurator.option.contrasteVeste.pochePoitrine' },
    ],
    'perso.construction': [
        { id: 'classique', labelKey: 'configurator.option.construction.classique' },
        { id: 'entoilage_traditionnel', labelKey: 'configurator.option.construction.entoilage' },
    ],
    'perso.interiorStyle': [
        { id: 'none', labelKey: 'configurator.option.interiorStyle.sans' },
        { id: 'full_lining_curve_facing', labelKey: 'configurator.option.interiorStyle.fullLining' },
        { id: 'straight', labelKey: 'configurator.option.interiorStyle.straight' },
    ],
    'perso.collarFelt': [
        { id: 'match', labelKey: 'configurator.option.collarFelt.match' },
        { id: 'other', labelKey: 'configurator.option.collarFelt.other' },
    ],

    // ----- Personnalisation gilet -----
    'perso.vestPockets': [
        { id: 'none', labelKey: 'configurator.option.pocheGilet.sans' },
        { id: 'passepoilee', labelKey: 'configurator.option.pocheGilet.passepoilee' },
        { id: 'double_passepoilee', labelKey: 'configurator.option.pocheGilet.doublePassepoilee' },
        { id: 'passepoilee_et_poitrine', labelKey: 'configurator.option.pocheGilet.passepoileePoitrine' },
        { id: 'poche_a_rabat', labelKey: 'configurator.option.pocheGilet.pocheARabat' },
    ],
    'perso.vestLapels': [
        { id: 'none', labelKey: 'configurator.option.reversGilet.sans' },
        { id: 'a_cran', labelKey: 'configurator.option.reversGilet.aCran' },
        { id: 'chale', labelKey: 'configurator.option.reversGilet.chale' },
        { id: 'en_pointe', labelKey: 'configurator.option.reversGilet.enPointe' },
        { id: 'grand_u', labelKey: 'configurator.option.reversGilet.grandU' },
        { id: 'u', labelKey: 'configurator.option.reversGilet.u' },
    ],
    'perso.vestBack': [
        { id: 'none', labelKey: 'configurator.option.arriereGilet.sans' },
        { id: 'carre', labelKey: 'configurator.option.arriereGilet.carre' },
        { id: 'pointe', labelKey: 'configurator.option.arriereGilet.pointe' },
    ],
    'perso.vestContrast': [
        { id: 'col', labelKey: 'configurator.option.contrasteGilet.col' },
        { id: 'col_arriere', labelKey: 'configurator.option.contrasteGilet.colArriere' },
        { id: 'poche_basse', labelKey: 'configurator.option.contrasteGilet.pocheBasse' },
        { id: 'poche_poitrine', labelKey: 'configurator.option.contrasteGilet.pochePoitrine' },
    ],

    // ----- Personnalisation pantalon -----
    'perso.cornerPocket': [
        { id: 'sans', labelKey: 'configurator.option.pochetteCoin.sans' },
        { id: 'poche_interrieur', labelKey: 'configurator.option.pochetteCoin.pocheInterrieur' },
        { id: 'poche_ticket', labelKey: 'configurator.option.pochetteCoin.pocheTicket' },
        { id: 'poche_a_rabat', labelKey: 'configurator.option.pochetteCoin.pocheARabat' },
    ],
    'perso.backPocket': [
        { id: 'aucun', labelKey: 'configurator.option.pocheArriere.aucun' },
        { id: 'double_passepoile_droit', labelKey: 'configurator.option.pocheArriere.doublePassepoileDroit' },
        { id: 'double_passepoilee', labelKey: 'configurator.option.pocheArriere.doublePassepoilee' },
        { id: 'double_passepoilee_avec_bouton', labelKey: 'configurator.option.pocheArriere.doublePassepoileeAvecBouton' },
        { id: 'passepoilee_avec_bouton', labelKey: 'configurator.option.pocheArriere.passepoileeAvecBouton' },
        { id: 'rabat_1', labelKey: 'configurator.option.pocheArriere.rabat1' },
        { id: 'rabat_2', labelKey: 'configurator.option.pocheArriere.rabat2' },
        { id: 'rabat_sans_bouton', labelKey: 'configurator.option.pocheArriere.rabatSansBouton' },
    ],
    'perso.pantsBottom': [
        { id: 'ourlet', labelKey: 'configurator.option.basDePantalon.ourlet' },
        { id: 'revers', labelKey: 'configurator.option.basDePantalon.revers' },
        { id: 'non_fini', labelKey: 'configurator.option.basDePantalon.nonFini' },
    ],
    'perso.pantsLining': [
        { id: 'sans', labelKey: 'configurator.option.doublurePantalon.sans' },
        { id: 'avec', labelKey: 'configurator.option.doublurePantalon.avec' },
    ],
    'perso.jacketLining': [{ id: 'match', label: 'Assortie au costume' }],
    'perso.pantsLiningFabric': [{ id: 'match', label: 'Assortie au costume' }],
    'perso.pantsContrast': [
        { id: 'passepoilee_poche', labelKey: 'configurator.option.contrastePantalon.passepoileePoche' },
        { id: 'bande_cote_0.5cm', labelKey: 'configurator.option.contrastePantalon.bandeCote05' },
        { id: 'bande_cote_1.2cm', labelKey: 'configurator.option.contrastePantalon.bandeCote12' },
        { id: 'bande_cote_2.0cm', labelKey: 'configurator.option.contrastePantalon.bandeCote20' },
        { id: 'ceinture', labelKey: 'configurator.option.contrastePantalon.ceinture' },
        { id: 'bouton_recouvert', labelKey: 'configurator.option.contrastePantalon.boutonRecouvert' },
    ],
    'coupe.jacketCut.buttonholeColor': threadColorItems,
    'coupe.addVest.buttonholeColor': threadColorItems,
    'coupe.pantsCut.waistHeight': ['3.5', '5.0'].map((id) => ({ id, label: `${id} cm` })),
    'perso.colType.size': collarSizeItems,
    'perso.topstitch.color': threadColorItems,
    'perso.collarButtonhole.color': threadColorItems,
    'perso.sleeveButtons.number': ['none', '1', '2', '3', '4', '5'].map((id) => ({ id, label: id === 'none' ? 'Sans' : id })),
    'perso.sleeveButtons.overlap': [{ id: 'non', label: 'Non chevauché' }, { id: 'oui', label: 'Chevauché' }],
    'perso.sleeveButtons.orientation': [{ id: 'droit', label: 'Droit' }, { id: 'biais', label: 'En biais' }],
    'perso.sleeveButtons.color': threadColorItems,
    'perso.sleeveButtons.position': [{ id: 'first', label: 'Premier' }, { id: 'last', label: 'Dernier' }, { id: 'all', label: 'Tous' }],
    'perso.vestLapels.size': vestCollarSizeItems,
    'vest.lapels.size': vestCollarSizeItems,
    'perso.pantsBottom.reversSize': ['2.5', '3.0', '3.5', '3.8', '4.0', '4.5', '5.0'].map((id) => ({ id, label: `${id} cm` })),
    'perso.embroidery.target': [
        { id: 'jacket', label: 'Veste' }, { id: 'pants', label: 'Pantalon' },
        { id: 'vest', label: 'Gilet' }, { id: 'both', label: 'Veste et pantalon' },
        { id: 'jacket_vest', label: 'Veste et gilet' }, { id: 'pants_vest', label: 'Pantalon et gilet' },
        { id: 'all', label: 'Veste, pantalon et gilet' },
    ],
    'perso.embroidery.sameSettings': [{ id: 'yes', label: 'Oui' }, { id: 'no', label: 'Non' }],
    'perso.embroidery.jacketPosition': [
        { id: 'on_chest_pocket', label: 'Poche poitrine' }, { id: 'small_fabric_below_left_inner', label: 'Intérieure gauche' },
        { id: 'outside_back_collar', label: 'Col arrière' }, { id: 'lower_edge_felt', label: 'Feutre de col' },
        { id: 'below_chest_pocket', label: 'Sous poche poitrine' },
    ],
    'perso.embroidery.pantsPosition': [
        { id: 'right_back_waist_center', label: 'Ceinture arrière' }, { id: 'bottom_fly', label: 'Braguette' },
        { id: 'above_right_back_pocket', label: 'Poche arrière droite' },
    ],
    'perso.embroidery.vestPosition': [
        { id: 'on_chest_pocket', label: 'Poche poitrine' },
        { id: 'small_fabric_below_left_inner', label: 'Intérieure gauche' },
        { id: 'below_chest_pocket', label: 'Sous poche poitrine' },
    ],
    'perso.embroidery.threadColor': embroideryColorItems,
    'perso.embroidery.size': ['8', '10', '12'].map((id) => ({ id, label: `${id} mm` })),
    'perso.embroidery.font': embroideryFontItems,
    'vest.embroidery.threadColor': embroideryColorItems,
    'vest.embroidery.size': ['8', '10', '12'].map((id) => ({ id, label: `${id} mm` })),
    'vest.embroidery.font': embroideryFontItems,
};

/**
 * Indique si un item d'une section est visible.
 * Convention identique à `isSectionVisible` : seul un `false` explicite masque.
 */
export const isItemVisible = (
    sectionKey: string,
    itemId: string,
    visibleItems?: Record<string, Record<string, boolean>> | null,
): boolean => visibleItems?.[sectionKey]?.[itemId] !== false;

/**
 * Filtre une liste d'options du configurateur en retirant celles que le membre
 * a explicitement masquées. Conserve l'ordre d'origine.
 */
export const filterVisibleOptions = <T extends { id: string }>(
    sectionKey: string,
    options: T[],
    visibleItems?: Record<string, Record<string, boolean>> | null,
): T[] => {
    const sectionMap = visibleItems?.[sectionKey];
    if (!sectionMap) return options;
    return options.filter((opt) => sectionMap[opt.id] !== false);
};

/* =====================================================================
 *  Valeurs par défaut de section (voir app boutique pour la logique complète).
 *  Une section masquée utilise une valeur par défaut dans la commande.
 * ===================================================================== */

export const SECTION_DEFAULTABLE: string[] = [
    'coupe.suitType', 'coupe.jacketCut', 'coupe.pantsCut', 'coupe.addVest', 'coupe.vestFabric', 'coupe.vestBackFabric', 'coupe.pantsFabric',
    'perso.colType', 'perso.jacketBottom', 'perso.chestPocket', 'perso.lowerPockets',
    'perso.sleeveCap', 'perso.backVents', 'perso.buttons', 'perso.topstitch', 'perso.collarButtonhole',
    'perso.sleeveOpening', 'perso.sleeveButtons', 'perso.construction', 'perso.interiorStyle', 'perso.jacketLining', 'perso.collarFelt',
    'perso.vestPockets', 'perso.vestButtons', 'perso.vestLapels', 'perso.vestBack', 'perso.cornerPocket', 'perso.backPocket',
    'perso.pantsBottom', 'perso.pantsLining', 'perso.pantsLiningFabric', 'perso.pantsButtons',
    'perso.embroidery.target',
    'shirt.style', 'shirt.fabric', 'vest.cut', 'vest.buttons', 'vest.pockets', 'vest.lapels',
    'vest.back', 'vest.buttonhole', 'vest.embroidery',
];

export const SECTION_DEFAULT_VALUES: Record<string, string> = {
    'coupe.suitType': '2_pieces',
    'coupe.jacketCut': '1_bouton',
    'coupe.pantsCut': 'sans_pince',
    'coupe.addVest': 'none',
    'coupe.vestFabric': 'same',
    'coupe.vestBackFabric': 'same',
    'coupe.pantsFabric': 'same',
    'shirt.style': 'chemise_1',
    'shirt.fabric': 'F41601',
    'vest.cut': '3_boutons',
    'vest.buttons': 'none',
    'vest.pockets': 'none',
    'vest.lapels': 'none',
    'vest.back': 'none',
    'vest.buttonhole': 'match',
    'vest.embroidery': 'none',
    'perso.colType': 'col_a_cran',
    'perso.jacketBottom': 'round',
    'perso.chestPocket': 'sans',
    'perso.lowerPockets': 'standard',
    'perso.sleeveCap': 'standard',
    'perso.backVents': 'fermer',
    'perso.buttons': 'none',
    'perso.topstitch': 'none',
    'perso.collarButtonhole': 'none',
    'perso.sleeveOpening': 'non_fonctionnel',
    'perso.sleeveButtons': 'none',
    'perso.construction': 'classique',
    'perso.interiorStyle': 'none',
    'perso.jacketLining': 'match',
    'perso.collarFelt': 'match',
    'perso.vestPockets': 'none',
    'perso.vestButtons': 'none',
    'perso.vestLapels': 'none',
    'perso.vestBack': 'none',
    'perso.cornerPocket': 'sans',
    'perso.backPocket': 'aucun',
    'perso.pantsBottom': 'ourlet',
    'perso.pantsLining': 'sans',
    'perso.pantsLiningFabric': 'match',
    'perso.pantsButtons': 'none',
    'perso.embroidery.target': 'jacket',
};

/** Indique si une section peut avoir une valeur par défaut configurable. */
export const isSectionDefaultable = (sectionKey: string): boolean =>
    SECTION_DEFAULTABLE.includes(sectionKey) && (SECTION_ITEMS[sectionKey]?.length ?? 0) > 0;

/* =====================================================================
 *  Helpers tarification
 * ===================================================================== */

export interface PricingMap {
    showPrices: boolean;
    sections: Record<string, number>;
    items: Record<string, Record<string, number>>;
}

/**
 * Retourne le prix à afficher pour un item dans une section :
 *  - prix per-item (`pricing.items[sectionKey][itemId]`) en priorité
 *  - sinon prix par défaut de la section (`pricing.sections[sectionKey]`)
 *  - sinon undefined (rien à afficher).
 */
export const getOptionPrice = (
    sectionKey: string,
    itemId: string,
    pricing?: { sections?: Record<string, number>; items?: Record<string, Record<string, number>> } | null,
): number | undefined => {
    const itemPrice = pricing?.items?.[sectionKey]?.[itemId];
    if (typeof itemPrice === 'number') return itemPrice;
    const sectionPrice = pricing?.sections?.[sectionKey];
    if (typeof sectionPrice === 'number') return sectionPrice;
    return undefined;
};

/**
 * Enrichit une liste d'options avec leur prix si la tarification est activée.
 * Si `showPrices` est faux, retourne la liste inchangée.
 */
export const enrichOptionsWithPrices = <T extends { id: string }>(
    sectionKey: string,
    options: T[],
    pricing?: { showPrices?: boolean; sections?: Record<string, number>; items?: Record<string, Record<string, number>> } | null,
): (T & { price?: number })[] => {
    if (!pricing?.showPrices) return options;
    return options.map((opt) => {
        const price = getOptionPrice(sectionKey, opt.id, pricing);
        return price !== undefined ? { ...opt, price } : opt;
    });
};

/**
 * Calcule le total d'une configuration en sommant les prix des options
 * sélectionnées (sections + items) et des tissus.
 */
export interface ConfiguratorTotalSelections {
    /** Map sectionKey -> selected itemId (string) ou tableau d'itemIds (multi). */
    sections: Record<string, string | string[] | null | undefined>;
    /** Tissu principal sélectionné. */
    tissuId?: string | null;
    /** Tissu de gilet (si different). */
    vestFabricId?: string | null;
}

export const getConfiguratorTotal = (
    selections: ConfiguratorTotalSelections,
    pricing?: {
        showPrices?: boolean;
        sections?: Record<string, number>;
        items?: Record<string, Record<string, number>>;
        fabrics?: Record<string, number>;
    } | null,
): number => {
    if (!pricing?.showPrices) return 0;
    let total = 0;
    for (const [sectionKey, selected] of Object.entries(selections.sections)) {
        if (!selected) continue;
        const ids = Array.isArray(selected) ? selected : [selected];
        for (const id of ids) {
            if (!id || id === 'none' || id === 'aucun' || id === 'sans') continue;
            const p = getOptionPrice(sectionKey, id, pricing);
            if (typeof p === 'number') total += p;
        }
    }
    if (selections.tissuId) {
        const p = pricing.fabrics?.[selections.tissuId];
        if (typeof p === 'number') total += p;
    }
    if (selections.vestFabricId) {
        const p = pricing.fabrics?.[selections.vestFabricId];
        if (typeof p === 'number') total += p;
    }
    return total;
};
